'use client'

/**
 * Client side of the return to lists (lib/navigation/list-context.ts).
 *
 * - `useListContinuity()` is mounted ONCE by the Encuentro frame (F7), which
 *   persists across navigations: it records every visit and keeps each list's
 *   scroll position.
 * - `useListReturnHref(destination)` gives a detail the href of its list with
 *   the filters the member left it with (a plain link back).
 * - `useListBack(destination)` is the back control of a detail: history back
 *   when the previous entry is that list, otherwise a push of the remembered
 *   list, otherwise the canonical list.
 *
 * The memory is module state, so it survives soft navigations and is gone
 * after a reload. It is only written in effects, never during render, and the
 * server snapshot is always the canonical href (no hydration mismatch).
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useListScroll } from '@/hooks/use-list-scroll'
import {
  cameFromOutsidePeople,
  canonicalListHref,
  createListMemory,
  listScrollKey,
  listSnapshot,
  normalizedHref,
  resolveListReturn,
  resolveTaskReturn,
} from '@/lib/navigation/list-context'
import { parseMemberPath, type Destination } from '@/lib/navigation/routes'
import { returnLabelOf, taskPageOf, type ReturnLabel } from '@/lib/navigation/task-pages'

const memory = createListMemory()

/** Test seam: the memory this module reads and writes. */
export const listMemoryForTests = memory

export function useListContinuity() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const search = searchParams.toString()
  const router = useRouter()

  useEffect(() => {
    // `useSearchParams()` is empty on the first paint of a production build;
    // the effect reads the real query from the location instead.
    memory.visit(window.location.pathname, window.location.search)
  }, [pathname, search])

  // Lists always get their position back. Any other page (a profile, a job)
  // only records it, and gets it back when a task page returns to it
  // (`useTaskReturn`), refreshed when the task wrote something.
  const snapshot = listSnapshot(pathname, search)
  const href = normalizedHref(pathname, search)
  const returning = memory.peekReturn(href) !== null
  useListScroll(listScrollKey(snapshot) ?? `page:${href}`, snapshot !== null || returning)
  useEffect(() => {
    if (memory.takeReturn(href)?.refresh) router.refresh()
  }, [href, router])
}

function useOrgSlug(): string | null {
  return parseMemberPath(usePathname())?.org ?? null
}

export function useListReturnHref(destination: Destination): string | null {
  const org = useOrgSlug()
  const remembered = useSyncExternalStore(
    memory.subscribe,
    () => (org ? memory.listHref(org, destination) : null),
    () => null
  )
  if (!org) return null
  return remembered ?? canonicalListHref(org, destination)
}

/** The page the member came from, when it was in the app and is not People (ALU-241). */
export function usePreviousOutsidePeople(): string | null {
  const previous = useSyncExternalStore(
    memory.subscribe,
    () => memory.previousHref(),
    () => null
  )
  return cameFromOutsidePeople(previous) ? previous : null
}

export function useListBack(destination: Destination): () => void {
  const router = useRouter()
  const org = useOrgSlug()
  const href = useListReturnHref(destination)

  useEffect(() => {
    if (href) router.prefetch(href)
  }, [href, router])

  return useCallback(() => {
    if (!org) {
      router.back()
      return
    }
    const target = resolveListReturn({
      remembered: memory.listHref(org, destination),
      canonical: canonicalListHref(org, destination),
      previousHref: memory.previousHref(),
    })
    if (target.kind === 'back') router.back()
    else router.push(target.href, { scroll: false })
  }, [org, destination, router])
}

export type TaskReturn = {
  /** Where "‹ …" leads right now (for the link's href and its label). */
  href: string | null
  label: ReturnLabel | null
  /**
   * Leave the task for the page it was opened from: history back when that
   * page is right behind (its scroll comes back), otherwise a push of it, or
   * of the task's fallback on a cold entry. `refresh` re-reads that page
   * (the task saved something it shows).
   */
  back: (options?: { refresh?: boolean }) => void
  /** The task ended somewhere else (a new job's page): forget its origin. */
  finish: () => void
}

/**
 * The return of a task page (lib/navigation/task-pages.ts; ALU-4). Outside a
 * task page `href` is null and `back` is history back.
 */
export function useTaskReturn(): TaskReturn {
  const router = useRouter()
  const pathname = usePathname()
  const task = taskPageOf(pathname)
  const origin = useSyncExternalStore(
    memory.subscribe,
    () => (task ? memory.originOf(pathname) : null),
    () => null
  )
  const href = task ? (origin ?? task.fallback) : null

  useEffect(() => {
    if (href) router.prefetch(href)
  }, [href, router])

  const back = useCallback(
    (options?: { refresh?: boolean }) => {
      const current = taskPageOf(pathname)
      if (!current) {
        router.back()
        return
      }
      const target = resolveTaskReturn({
        origin: memory.originOf(pathname),
        fallback: current.fallback,
        previousHref: memory.previousHref(),
      })
      const returnTo = target.kind === 'back' ? memory.originOf(pathname)! : target.href
      memory.forgetOrigin(pathname)
      memory.markReturn(returnTo, { refresh: Boolean(options?.refresh) })
      if (target.kind === 'back') router.back()
      else router.push(target.href, { scroll: false })
    },
    [pathname, router]
  )
  const finish = useCallback(() => memory.forgetOrigin(pathname), [pathname])

  return { href, label: href ? returnLabelOf(href) : null, back, finish }
}
